import { useState } from 'react';
import type { ReportChapterRow } from '../../../api/types';
import { Badge, Button, Checkbox, PanelCard, SortableTh, Table, Td, TEXT_LINK } from '../../../components/ui';
import { useSortableTable, SortColumn } from '../../../lib/useSortableTable';

interface Props { chapters: ReportChapterRow[]; passPct: number; onRemediate?: (chapterIds: number[]) => void }

const REPORT_CHAPTERS_COLUMNS: SortColumn<ReportChapterRow>[] = [
  { key: 'chapter', accessor: (c) => c.chapter_name },
  { key: 'questions', accessor: (c) => c.question_count, type: 'number' },
  { key: 'class_avg', accessor: (c) => c.class_avg, type: 'number' },
  { key: 'below', accessor: (c) => c.below_pass.length, type: 'number' },
];

/** Names shown before a "+N more" toggle collapses the rest. */
const NAMES_SHOWN = 3;

/** Class average as the design's status pill: critical below the pass mark, warning within 10 points of it. */
function avgTone(avg: number, passPct: number): 'danger' | 'warning' | 'success' {
  return avg < passPct ? 'danger' : avg < passPct + 10 ? 'warning' : 'success';
}

function BelowPassCell({ students }: { students: ReportChapterRow['below_pass'] }) {
  const [expanded, setExpanded] = useState(false);
  if (students.length === 0) return <span className="text-muted">none</span>;
  const shown = expanded ? students : students.slice(0, NAMES_SHOWN);
  const hidden = students.length - shown.length;
  return (
    <div>
      <div><Badge tone="danger">{students.length}</Badge> <span className="text-muted">students</span></div>
      <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-muted-2">
        {shown.map((s) => <span key={s.student_id}>{s.name} ({s.pct.toFixed(0)}%)</span>)}
        {(hidden > 0 || expanded) && (
          <button type="button" className={`${TEXT_LINK} !min-h-0 p-0`}
            aria-expanded={expanded} onClick={() => setExpanded((v) => !v)}>
            {expanded ? 'Show fewer' : `+${hidden} more`}
          </button>
        )}
      </div>
    </div>
  );
}

export default function ReportChaptersBlock({ chapters: inputChapters, passPct, onRemediate }: Props) {
  const [selected, setSelected] = useState<number[]>([]);
  const toggle = (id: number) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const { rows: chapters, sort, toggle: onSort } = useSortableTable(inputChapters, REPORT_CHAPTERS_COLUMNS, { key: 'class_avg', dir: 'asc' });
  const th = 'border-b border-line';
  return (
    <PanelCard
      aria-label="Chapters"
      title="Chapters"
      subtitle={onRemediate ? 'Weakest first. Tick the ones to work on, then start a remedial cycle.' : undefined}
      actions={onRemediate ? (
        <Button size="md" variant={selected.length > 0 ? 'secondary' : 'ghost'} disabled={selected.length === 0} onClick={() => onRemediate(selected)}>
          Remediate selected ({selected.length})
        </Button>
      ) : undefined}
    >
      <Table minWidth={560}>
        <thead>
          <tr>
            {onRemediate && <th className={`${th} w-[30px] px-2.5 pb-2.5`}><span className="sr-only">Select</span></th>}
            <SortableTh className={th} columnKey="chapter" sort={sort} onToggle={onSort}>Chapter</SortableTh>
            <SortableTh className={th} columnKey="questions" sort={sort} onToggle={onSort} align="right">Questions</SortableTh>
            <SortableTh className={th} columnKey="class_avg" sort={sort} onToggle={onSort} help="The class's average score on this chapter's questions in this exam.">Class avg</SortableTh>
            <SortableTh className={th} columnKey="below" sort={sort} onToggle={onSort} help="below_pass">Below {passPct}%</SortableTh>
          </tr>
        </thead>
        <tbody>
          {chapters.map((c) => (
            <tr key={c.chapter_id} className="align-top">
              {onRemediate && (
                <Td>
                  <Checkbox aria-label={`select ${c.chapter_name}`} checked={selected.includes(c.chapter_id)} onChange={() => toggle(c.chapter_id)} />
                </Td>
              )}
              <Td className="font-semibold text-ink">{c.chapter_name}</Td>
              <Td align="right" className="font-mono font-medium">{c.question_count}</Td>
              <Td className="whitespace-nowrap">
                {c.class_avg == null ? <span className="font-mono text-muted">{'—'}</span> : (
                  <Badge tone={avgTone(c.class_avg, passPct)}>{`${c.class_avg.toFixed(1)}%`}</Badge>
                )}
              </Td>
              <Td className="text-[13px]"><BelowPassCell students={c.below_pass} /></Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </PanelCard>
  );
}
